
/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';

import TextAreaStateless from './TextAreaStateless';
import announce from '../../../helpers/announce';

export default class TextAreaControlled extends React.PureComponent {
    static propTypes = {
        /** html id required to connect the input with the label */
        id: PropTypes.string.isRequired,

        /** Input name */
        name: PropTypes.string,

        /** Label contents, usually text */
        label: PropTypes.node.isRequired,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** html input type, i.e. "text" */
        type: PropTypes.oneOf(['text', 'password', 'search', 'email', 'tel']),

        /** Text to be used for the input placeholder */
        placeholder: PropTypes.string,

        /** The input's value. Use only when the input is controlled */
        value: PropTypes.string,

        /** Disables the field */
        isDisabled: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Makes the label and input arranged inline horizontally */
        isInline: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Contents of the hint message, usually text */
        hintText: PropTypes.node,

        /** Icon element to render before the children */
        iconElement: PropTypes.element,

        /** Adds a unit (such as '$') that appears to the left of the input */
        unitBefore: PropTypes.string,

        /** Adds a unit (such as '%') that appears to the right of the input */
        unitAfter: PropTypes.string,

        /** Ref to be passed to the input */
        innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.node]),

        /** Show clear button if true and there is a value */
        hasClearButton: PropTypes.bool,

        /** Clear button click handler */
        handleClearButtonClick: PropTypes.func,

        /** If true, the input will gain focus when it mounts */
        shouldFocusOnMount: PropTypes.bool,

        /** Additional className added to outer container for custom styling */
        className: PropTypes.string,

        /** Initial value the field should have on mount */
        defaultValue: PropTypes.string,

        /** Function fired when the field changes */
        onChange: PropTypes.func,

        /** Function fired when the field is blurred */
        onBlur: PropTypes.func,

        /** A function that accepts the field's value and returns an object with
        specific validation-related keys */
        validator: PropTypes.func,

        /** A function that accepts the field's value and returns a string with
        appropriate hintText content or null */
        hinter: PropTypes.func,

        /** Performs the validation as soon as the component mounts */
        shouldValidateOnMount: PropTypes.bool,

        /** Enables validation as you type */
        shouldValidateOnUpdate: PropTypes.bool,

        /** Enables hinting as you type */
        shouldHintOnUpdate: PropTypes.bool,

        /** If the field's value should be changed after validation to match the
        formatted value returned by the validator. Usually to format something like
        a phone or number. */
        shouldReformatValue: PropTypes.bool,

        /** Callback invoked when isInvalid/errorText changes state. Returns an
        object with the id, isValid, and errorText */
        handleNewErrorState: PropTypes.func
    };

    static defaultProps = {
        isDisabled: false,
        isLabelVisible: true,
        value: undefined,
        type: 'text',
        placeholder: undefined,
        isRequired: false,
        isInvalid: false,
        isInline: false,
        errorText: undefined,
        hintText: undefined,
        iconElement: undefined,
        innerRef: undefined,
        hasClearButton: false,
        handleClearButtonClick: undefined,
        className: undefined,
        defaultValue: '',
        onChange: undefined,
        onBlur: undefined,
        shouldFocusOnMount: false,
        validator: undefined,
        hinter: undefined,
        shouldValidateOnMount: false,
        shouldValidateOnUpdate: false,
        shouldHintOnUpdate: false,
        shouldReformatValue: true,
        handleNewErrorState: undefined
    };

    constructor(props) {
        super(props);

        const hasErrorText = typeof props.errorText === 'string' && props.errorText.length > 0;

        this.state = {
            value: props.value || props.defaultValue,
            isPristine: !(props.value || props.defaultValue),
            isInvalid: props.isInvalid || hasErrorText || false,
            errorText: props.errorText || undefined,
            hintText: props.hintText || undefined
        };

        this.errorState = {
            isInvalid: props.isInvalid,
            errorText: props.errorText
        };
    }

    componentDidMount() {
        const { shouldValidateOnMount } = this.props;
        const { value } = this.state;

        if (value && shouldValidateOnMount) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.validate();
            }, 0);
        }
    }

    componentDidUpdate(prevProps) {
        const { shouldValidateOnUpdate, shouldHintOnUpdate, isInvalid, errorText } = this.props;

        if (shouldHintOnUpdate) {
            this.hint();
        }

        if (shouldValidateOnUpdate) {
            this.validate();
        }
        else if (isInvalid !== prevProps.isInvalid || errorText !== prevProps.errorText) {
            const hasErrorText = typeof errorText === 'string' && errorText.length > 0;

            this.setState({
                isInvalid: isInvalid || hasErrorText,
                errorText
            });
        }
    }

    componentWillUnmount() {
        // Safety in case of render thrashing
        clearTimeout(this.timeout);
    }

    isControlled() {
        const { value, onChange } = this.props;
        if (typeof value === 'string' && onChange !== undefined) {
            return true;
        }
        return false;
    }

    clearError = () => {
        this.setState({
            isInvalid: false,
            errorText: undefined
        });
    }

    handleNewErrorState = ({ isInvalid, errorText }) => {
        const { handleNewErrorState, id, name } = this.props;

        // bail if no handleNewErrorState exists
        if (!handleNewErrorState) {
            return;
        }

        // compare new state to old state. If different, invoke callback
        if (isInvalid !== this.errorState.isInvalid || errorText !== this.errorState.errorText) {
            handleNewErrorState({
                id,
                name,
                isInvalid,
                errorText
            });

            this.errorState = {
                isInvalid,
                errorText
            };
        }
    }

    handleClearButtonClick = (event) => {
        event.preventDefault();

        const { onChange } = this.props;

        this.setState({
            value: '',
            isPristine: true,
            isInvalid: false,
            errorText: undefined
        });

        if (onChange) {
            onChange({ target: { value: '' } });
        }

        this.handleNewErrorState({
            isValid: false,
            errorText: undefined
        });
    }

    handleChange = (event) => {
        event.persist();
        const value = event.target.value;
        const { onChange } = this.props;
        const { isPristine } = this.state;

        if (this.isControlled()) {
            onChange(event);
            if (value && isPristine) {
                this.setState({ isPristine: false });
            }
        }
        else {
            this.setState({ value }, () => {
                if (value && isPristine) {
                    this.setState({ isPristine: false });
                }
            });

            if (onChange) {
                onChange(event);
            }
        }
    };

    handleBlur = (event) => {
        const { isRequired, hinter, validator, onBlur } = this.props;
        const { isPristine, isInvalid, errorText } = this.state;

        const value = this.isControlled() ? this.props.value : this.state.value;

        if (hinter) {
            this.hint();
        }

        if ((isRequired || validator) && !isPristine) {
            // this.validate() performs the validation and handles errors, but we're
            // also returning a copy of the state for use in onBlur. This is to avoid
            // more complex setState callbacks and having to call onBlur inside
            // this.validate()
            const validationState = this.validate();

            if (onBlur) {
                onBlur(event, { value, ...validationState });
            }

            if (validationState.isInvalid && validationState.errorText) {
                announce(validationState.errorText, 'assertive');
            }
        }
        else if (onBlur) {
            onBlur(event, { value, isInvalid, errorText });
        }
    };

    // Generates the hint text based off of value if a hinter is supplied
    hint = () => {
        const { hinter, hintText } = this.props;
        const value = this.isControlled() ? this.props.value : this.state.value;

        if (value && hinter) {
            let updatedHintText = hintText;

            updatedHintText = hinter(value);
            this.setState({
                hintText: updatedHintText
            });
        }
    }

    validate = () => {
        const { validator, isRequired, shouldReformatValue, id } = this.props;
        const value = this.isControlled() ? this.props.value : this.state.value;

        // Because there's tricky timing issues with setting state and returning
        // updated state values that a controlled component can use, we're using and
        // returning a local variable and setting state with that variable once at
        // the end.

        const validationState = {
            isInvalid: false,
            errorText: null,
            id
        };

        // When the field is empty
        if (!value) {
            if (isRequired) {
                const errorText = typeof this.props.label === 'string'
                    ? `${this.props.label} is required.`
                    : 'This field is required';
                validationState.isInvalid = true;
                validationState.errorText = errorText;
            }
            else {
                // Reset error state
                this.clearError();
            }
        }

        // When the field has a value and there's a validator func
        if (value && validator) {
            const parsed = validator(value);

            // Value passes validation
            if (parsed.isValid) {
                validationState.isInvalid = false;
                validationState.errorText = null;

                // Reformat the value if allowed and present
                if (shouldReformatValue && parsed.formattedValue) {
                    validationState.value = parsed.formattedValue;

                    // Collect attributes from the input so we can generate
                    // a faux event.target to send to handleChange. This will
                    // allow external event handlers to continue to get
                    // info like event.target.id and event.target.name
                    const element = document.getElementById(this.props.id);
                    const attrs = element.attributes;
                    const attributes = {};

                    for (let i = 0; i < attrs.length; i += 1) {
                        attributes[attrs[i].name] = attrs[i].value;
                    }

                    this.handleChange({
                        target: {
                            ...attributes,
                            value: parsed.formattedValue
                        },
                        persist: () => { }
                    });
                }
            }

            // Validation fails
            if (!parsed.isValid) {
                validationState.isInvalid = true;
                validationState.errorText = parsed.errorText || null;
            }
        }

        // Update local component state
        this.setState(validationState);
        this.handleNewErrorState(validationState);

        // Return values to be passed on to onBlur
        return validationState;
    };

    render() {
        const {
            defaultValue,
            shouldReformatValue,
            shouldValidateOnMount,
            shouldValidateOnUpdate,
            shouldHintOnUpdate,
            validator,
            hinter,
            handleNewErrorState,
            ...rest } = this.props;

        const { isInvalid, errorText, hintText } = this.state;
        const value = this.isControlled() ? this.props.value : this.state.value;

        return (
            <TextAreaStateless
                {...rest}
                onChange={this.handleChange}
                onBlur={this.handleBlur}
                handleClearButtonClick={this.handleClearButtonClick}
                isInvalid={isInvalid}
                errorText={errorText}
                hintText={hintText}
                value={value} />
        );
    }
}
